<template>
    <BaseModal
        v-if="showModal"
        :title="
            isEditing
                ? 'Edit Student Class Enrollment'
                : 'Add Student Class Enrollment'
        "
        :loading="isSubmitting"
        :submit-text="isEditing ? 'Update Enrollment' : 'Create Enrollment'"
        max-width="7xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <!-- Student Selection -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <SearchableSelect
                            v-model="form.student_id"
                            :options="studentOptions"
                            label="Student"
                            placeholder="Select a student"
                            :optionLabel="
                                (student) =>
                                    `${student.user?.name} (${student.student_id})`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.student_id"
                        />
                    </div>

                    <!-- Class Selection -->
                    <div>
                        <SearchableSelect
                            v-model="form.class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="Select a class"
                            :optionLabel="
                                (classOption) =>
                                    `${classOption.grade_level} - ${classOption.name}`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.class_id"
                        />
                    </div>
                </div>

                <!-- Semester Selection -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <SearchableSelect
                            v-model="form.semester_id"
                            :options="semesterOptions"
                            label="Semester"
                            placeholder="Select a semester"
                            :optionLabel="
                                (semester) =>
                                    `${semester.name} (${semester.academic_year?.name})`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.semester_id"
                        />
                    </div>

                    <!-- Enrollment Date -->
                    <div>
                        <label
                            for="enrollment_date"
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Enrollment Date
                            <span class="text-danger-500">*</span>
                        </label>
                        <input
                            id="enrollment_date"
                            v-model="form.enrollment_date"
                            type="date"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        <div
                            v-if="errors.enrollment_date"
                            class="text-danger-500 text-sm mt-1"
                        >
                            {{ errors.enrollment_date }}
                        </div>
                    </div>
                </div>

                <!-- Active Status -->
                <div class="flex items-center">
                    <input
                        id="is_active"
                        v-model="form.is_active"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-700 dark:text-white"
                    />
                    <label
                        for="is_active"
                        class="ml-2 block text-sm text-gray-900 dark:text-white"
                    >
                        Active Enrollment
                    </label>
                </div>

                <!-- Submit Error Message -->
                <div
                    v-if="errors.submit"
                    class="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-md"
                >
                    <p class="text-sm text-danger-600 dark:text-danger-400">
                        {{ errors.submit }}
                    </p>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { studentApi, type Student } from "@/api/student";
import { classSchoollApi, type ClassSchooll } from "@/api/classSchooll";
import semesterApi, { type Semester } from "@/api/semester";
import { useStudentClassStore } from "@/stores/studentClassStore";
import type {
    CreateStudentClassData,
    UpdateStudentClassData,
} from "@/api/studentClass";
import BaseModal from "@/components/BaseModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";

// Props
interface Props {
    showModal: boolean;
    studentClass?: any;
    isEditing: boolean;
}

const props = defineProps<Props>();

// Emits
interface Emits {
    close: [];
    saved: [];
}

const emit = defineEmits<Emits>();

// Store
const studentClassStore = useStudentClassStore();

// State
const isSubmitting = ref(false);
const errors = ref<Record<string, string>>({});
const studentOptions = ref<Student[]>([]);
const classOptions = ref<ClassSchooll[]>([]);
const semesterOptions = ref<Semester[]>([]);

// Form data
const form = ref<CreateStudentClassData>({
    student_id: "",
    class_id: "",
    semester_id: "",
    enrollment_date: "",
    is_active: true,
});

// Methods
const fetchOptions = async () => {
    try {
        const [studentsResponse, classesResponse, semestersResponse] =
            await Promise.all([
                studentApi.getAll({ per_page: 1000 }),
                classSchoollApi.getAll({ per_page: 1000 }),
                semesterApi.getSemesters({ per_page: 1000 }),
            ]);
        studentOptions.value = studentsResponse.data?.items || [];
        classOptions.value = classesResponse.data?.items || [];
        semesterOptions.value = semestersResponse.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.value.student_id) {
        newErrors.student_id = "Student is required";
    }

    if (!form.value.class_id) {
        newErrors.class_id = "Class is required";
    }

    if (!form.value.semester_id) {
        newErrors.semester_id = "Semester is required";
    }

    if (!form.value.enrollment_date) {
        newErrors.enrollment_date = "Enrollment date is required";
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;
    errors.value = {};

    try {
        if (props.isEditing && props.studentClass) {
            // Update existing enrollment
            const updateData: UpdateStudentClassData = {
                student_id: form.value.student_id,
                class_id: form.value.class_id,
                semester_id: form.value.semester_id,
                enrollment_date: form.value.enrollment_date
                    ? formatDateForBackend(form.value.enrollment_date)
                    : undefined,
                is_active: form.value.is_active,
            };

            await studentClassStore.updateStudentClass(
                props.studentClass.id,
                updateData,
            );
        } else {
            // Create new enrollment
            const createData: CreateStudentClassData = {
                student_id: form.value.student_id,
                class_id: form.value.class_id,
                semester_id: form.value.semester_id,
                enrollment_date: form.value.enrollment_date
                    ? formatDateForBackend(form.value.enrollment_date)
                    : undefined,
                is_active: form.value.is_active,
            };

            await studentClassStore.createStudentClass(createData);
        }

        resetForm();
        emit("saved");
        emit("close");
    } catch (error: any) {
        console.error("Form submission error:", error);

        if (error.response?.status === 422) {
            // Validation errors - show field-level errors
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                errors.value[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
        } else {
            // Server errors - show general error message
            errors.value = {
                submit:
                    error.response?.data?.message ||
                    "Failed to save student class enrollment",
            };
        }
    } finally {
        isSubmitting.value = false;
    }
};

// Helper function to format date for backend (YYYY-MM-DD)
const formatDateForBackend = (dateString: string): string => {
    if (!dateString) return "";

    // Handle both YYYY-MM-DD and ISO string formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const resetForm = () => {
    form.value = {
        student_id: "",
        class_id: "",
        semester_id: "",
        enrollment_date: "",
        is_active: true,
    };
    errors.value = {};
};

// Watch for modal open/close to fetch options
watch(
    () => props.showModal,
    (newVal) => {
        if (newVal) {
            fetchOptions();
        }
    },
);

// Watch for studentClass prop changes (when editing)
watch(
    () => props.studentClass,
    (newVal) => {
        if (newVal && props.isEditing) {
            form.value = {
                student_id: newVal.student_id || "",
                class_id: newVal.class_id || "",
                semester_id: newVal.semester_id || "",
                enrollment_date: newVal.enrollment_date?.split("T")[0] ?? "",
                is_active:
                    newVal.is_active !== undefined ? newVal.is_active : true,
            };
        }
    },
);

// Initialize form when editing
onMounted(() => {
    if (props.showModal) {
        fetchOptions();
    }
});
</script>
